import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderKanban, Users, Award, Clock, CheckCircle,
  AlertCircle, ChevronRight, Building2, FileText, Plus
} from 'lucide-react';
import api from '../../services/apiService';
import SuspendedCompanyOverlay from './shared/SuspendedCompanyOverlay';

// Stat Summary Card Component
const StatSummaryCard = ({ icon: Icon, value, label, badge, badgeColor, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm hover:shadow-md hover:border-orange-200 transition-all cursor-pointer"
  >
    <div className="flex items-center justify-between mb-3">
      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
        <Icon className="h-5 w-5 text-slate-600" />
      </div>
      {badge && (
        <span className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-wider rounded-md ${badgeColor || 'bg-slate-100 text-slate-600'}`}>
          {badge}
        </span>
      )}
    </div>
    <p className="text-2xl font-black text-slate-900">{value}</p>
    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{label}</p>
  </div>
);

// Technician Card Component
const TechnicianCard = ({ auth, formatDate, onClick }) => {
  const profilePicture = auth.user_profile_picture || auth.profile_picture;
  return (
    <div
      onClick={onClick}
      className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors border border-slate-100"
    >
      <div className="flex items-center gap-3">
        {profilePicture ? (
          <img
            src={profilePicture}
            alt={auth.user_name}
            className="w-10 h-10 rounded-xl object-cover border-2 border-orange-200"
          />
        ) : (
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-sm font-black">
            {auth.user_name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
        )}
      <div>
        <p className="text-sm font-black text-slate-900">{auth.user_name}</p>
        <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wide">{auth.authorization_level_name}</p>
      </div>
    </div>
    <div className="text-right">
      <span
        className="inline-block px-2.5 py-1 text-[10px] font-black rounded-lg text-white uppercase tracking-wide"
        style={{ backgroundColor: auth.authorization_level_color || '#F97316' }}
      >
        {auth.authorization_level_code}
      </span>
      <p className="text-[10px] text-slate-400 mt-1 font-medium">
        → {formatDate(auth.valid_until)}
      </p>
    </div>
  </div>
  );
};

// Nomination Card Component
const NominationCard = ({ nomination, formatDate, getStatusStyle, onClick }) => {
  const style = getStatusStyle(nomination.status);
  const profilePicture = nomination.user_profile_picture || nomination.profile_picture;
  return (
    <div
      onClick={onClick}
      className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors border border-slate-100"
    >
      <div className="flex items-center gap-3">
        {profilePicture ? (
          <img
            src={profilePicture}
            alt={nomination.user_name}
            className="w-10 h-10 rounded-xl object-cover border-2 border-blue-200"
          />
        ) : (
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-sm font-black">
            {nomination.user_name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
        )}
        <div>
          <p className="text-sm font-black text-slate-900">{nomination.user_name}</p>
          <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wide">{nomination.authorization_level_name}</p>
        </div>
      </div>
      <div className="text-right">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[9px] font-black uppercase tracking-wide rounded-lg ${style.bg} ${style.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
          {nomination.status.replace('_', ' ')}
        </span>
        <p className="text-[10px] text-slate-400 mt-1 font-medium">
          {formatDate(nomination.created_at)}
        </p>
      </div>
    </div>
  );
};

const WindCompanyOEMDashboard = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [nominations, setNominations] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const dashboardResponse = await api.get('/oem/dashboard/company/');
        setDashboardData(dashboardResponse.data);

        const projectsResponse = await api.get('/oem/projects/');
        setProjects(projectsResponse.data);

        const nominationsResponse = await api.get('/oem/nominations/');
        setNominations(nominationsResponse.data);

        const authResponse = await api.get('/oem/authorizations/?is_active=true');
        setAuthorizations(authResponse.data);
      } catch (err) {
        console.error('Error fetching data:', err);
        if (err.response?.status === 403) {
          setError('Your company is not participating in any OEM projects yet.');
        } else {
          setError('Failed to load dashboard data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getStatusStyle = (status) => {
    const styles = {
      pending: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
      submitted: { bg: 'bg-blue-50', text: 'text-blue-600', dot: 'bg-blue-500' },
      under_review: { bg: 'bg-amber-50', text: 'text-amber-600', dot: 'bg-amber-500' },
      approved: { bg: 'bg-emerald-50', text: 'text-emerald-600', dot: 'bg-emerald-500' },
      rejected: { bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-500' },
      revoked: { bg: 'bg-purple-50', text: 'text-purple-600', dot: 'bg-purple-500' },
      expired: { bg: 'bg-slate-100', text: 'text-slate-500', dot: 'bg-slate-400' },
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm border border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-600 text-sm font-medium">Loading dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 max-w-md mx-auto text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Building2 className="h-8 w-8 text-slate-400" />
          </div>
          <h2 className="text-lg font-black text-slate-900 mb-2 uppercase tracking-tight">OEM Projects</h2>
          <p className="text-slate-500 text-sm mb-4">{error}</p>
          <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">
            Contact your OEM Master administrator to be added to a project.
          </p>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};

  return (
    <SuspendedCompanyOverlay>
      <div className="p-6 max-w-7xl mx-auto space-y-5">
        {/* Action Button */}
        {projects.length > 0 && (
          <div className="flex items-center justify-end">
            <button
              onClick={() => navigate('/oem-company/nominations/create')}
              className="h-9 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 flex items-center gap-1.5 font-black uppercase tracking-wide text-[10px] transition-all shadow-lg shadow-orange-500/20"
            >
              <Plus className="h-3.5 w-3.5" />
              New Nomination
            </button>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-3">
          <StatSummaryCard
            icon={FolderKanban}
            value={stats.participating_projects || 0}
            label="Projects"
            badge="Active"
            badgeColor="bg-emerald-100 text-emerald-600"
            onClick={() => navigate('/oem-company/projects')}
          />
          <StatSummaryCard
            icon={Users}
            value={stats.authorized_technicians || 0}
            label="Authorized"
            badge={<><CheckCircle className="h-2.5 w-2.5 inline mr-0.5" />Ready</>}
            badgeColor="bg-emerald-100 text-emerald-600"
            onClick={() => navigate('/oem-company/nominations?status=approved')}
          />
          <StatSummaryCard
            icon={Clock}
            value={stats.pending_nominations || 0}
            label="Pending"
            badge={stats.pending_nominations > 0 ? 'Review' : null}
            badgeColor="bg-amber-100 text-amber-600"
            onClick={() => navigate('/oem-company/nominations?status=submitted')}
          />
          <StatSummaryCard
            icon={Award}
            value={authorizations.length}
            label="Authorizations"
            badge="Active"
            badgeColor="bg-violet-100 text-violet-600"
            onClick={() => navigate('/oem-company/nominations')}
          />
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-2 gap-5">
          {/* Authorized Technicians */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Users className="h-4 w-4 text-emerald-600" />
                </div>
                <h2 className="text-sm font-black text-slate-900 uppercase tracking-tight">Authorized Technicians</h2>
                <span className="px-2 py-0.5 text-[9px] font-black rounded-md bg-emerald-100 text-emerald-600 uppercase tracking-wider">
                  {authorizations.length} active
                </span>
              </div>
              <button
                onClick={() => navigate('/oem-company/nominations')}
                className="text-[10px] font-black text-orange-500 hover:text-orange-600 flex items-center gap-0.5 uppercase tracking-wide transition-colors"
              >
                View all <ChevronRight className="h-3 w-3" />
              </button>
            </div>
            <div className="p-3">
              {authorizations.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Award className="h-6 w-6 text-slate-400" />
                  </div>
                  <p className="text-xs font-black text-slate-900 mb-1">No authorized technicians yet</p>
                  <p className="text-[10px] text-slate-400 font-medium">Create nominations to get started</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {authorizations.map((auth) => (
                    <TechnicianCard
                      key={auth.id}
                      auth={auth}
                      formatDate={formatDate}
                      onClick={() => navigate(`/oem-company/nominations/${auth.nomination_id || auth.id}`)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent Nominations */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FileText className="h-4 w-4 text-blue-600" />
                </div>
                <h2 className="text-sm font-black text-slate-900 uppercase tracking-tight">Recent Nominations</h2>
              </div>
              <button
                onClick={() => navigate('/oem-company/nominations')}
                className="text-[10px] font-black text-orange-500 hover:text-orange-600 flex items-center gap-0.5 uppercase tracking-wide transition-colors"
              >
                View all <ChevronRight className="h-3 w-3" />
              </button>
            </div>
            <div className="p-3">
              {nominations.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <FileText className="h-6 w-6 text-slate-400" />
                  </div>
                  <p className="text-xs font-black text-slate-900 mb-2">No nominations yet</p>
                  {projects.length > 0 && (
                    <button
                      onClick={() => navigate('/oem-company/nominations/create')}
                      className="text-[10px] font-black text-orange-500 hover:text-orange-600 uppercase tracking-wide"
                    >
                      Create your first nomination →
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {nominations.slice(0, 8).map((nomination) => (
                    <NominationCard
                      key={nomination.id}
                      nomination={nomination}
                      formatDate={formatDate}
                      getStatusStyle={getStatusStyle}
                      onClick={() => navigate(`/oem-company/nominations?selected=${nomination.id}`)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SuspendedCompanyOverlay>
  );
};

export default WindCompanyOEMDashboard;
